import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { FundCurrency } from '@agio/core/domain';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * The portal is right-to-left (`<html dir="rtl">`), and to the Unicode bidi
 * algorithm a minus sign is a *neutral* character: with only digits around it
 * there is nothing to anchor it, so it inherits the paragraph direction and is
 * painted on the wrong end — "-1.29%" comes out reading "1.29%-". Wrapping the
 * whole number in an LTR isolate pins the sign, the decimal point and the "%"
 * where a reader expects them, and stops the number from reordering against
 * neighbouring Hebrew.
 *
 * Isolate *characters* rather than a `<span dir="ltr">` because most of these
 * numbers are drawn by recharts into SVG `<text>` (axis ticks, tooltips, bar
 * labels), where there is no element to hang an attribute on — a plain string
 * is all the API accepts. One helper therefore covers both the DOM and the
 * charts.
 */
const LTR_ISOLATE = '\u2066';
const POP_ISOLATE = '\u2069';

/** Pin a formatted number to left-to-right order inside the RTL page. */
export function ltrNum(s: string): string {
  return `${LTR_ISOLATE}${s}${POP_ISOLATE}`;
}

/**
 * An amount in its fund's currency, Hebrew locale, no decimals: 37,691,171 ₪
 * or 894,398 $. Formatting only — nothing here converts.
 *
 * Deliberately *not* run through `ltrNum`: Intl already embeds the direction
 * marks he-IL needs, and forcing an outer LTR isolate moves the sign to the
 * wrong side of the amount.
 */
export function fmtMoney(v: number | null | undefined, currency: FundCurrency = 'ILS'): string {
  if (v === null || v === undefined) return '--';
  return new Intl.NumberFormat('he-IL', {
    style: 'currency', currency, currencyDisplay: 'narrowSymbol', maximumFractionDigits: 0,
  }).format(v);
}

/** Shekels — for whole-portfolio figures, which are always kept in shekels. */
export const fmtIls = (v: number | null | undefined): string => fmtMoney(v, 'ILS');

export function fmtPct(v: number | null | undefined, digits = 2): string {
  if (v === null || v === undefined) return '--';
  return ltrNum(`${(v * 100).toFixed(digits)}%`);
}

/** Plain number with Hebrew thousands separators: 37,691 — bidi-safe. */
export function fmtNum(v: number | null | undefined, digits = 0): string {
  if (v === null || v === undefined) return '--';
  const n = new Intl.NumberFormat('he-IL', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v);
  return ltrNum(n);
}

/** '2024-02-29' → '02/24' */
export function fmtMonth(iso: string): string {
  return `${iso.slice(5, 7)}/${iso.slice(2, 4)}`;
}

/** Month with a full year, no leading zero — "2024-03-01" → "3/2024". */
export function fmtMonthLong(iso: string): string {
  return `${Number(iso.slice(5, 7))}/${iso.slice(0, 4)}`;
}

// Chart colors live in chartTheme.ts (single source of truth); re-exported for convenience.
export { SERIES, PIE_COLORS, bySign, GRID, AXIS, TOOLTIP_STYLE } from './chartTheme.js';

/**
 * Merge two monthly series on YYYY-MM (port of the OSDK mergeTimeSeries) —
 * portfolio return vs benchmark, aligned by month key.
 */
export function mergeTimeSeries<A extends { date: string }, B extends { date: string }>(
  a: A[],
  b: B[],
): ({ month: string } & Partial<A> & Partial<B>)[] {
  type Row = { month: string } & Partial<A> & Partial<B>;
  const byMonth = new Map<string, Row>();
  for (const r of [...a, ...b] as ({ date: string } & (Partial<A> | Partial<B>))[]) {
    const k = r.date.slice(0, 7);
    byMonth.set(k, { ...(byMonth.get(k) ?? { month: k }), ...r } as Row);
  }
  return [...byMonth.values()].sort((x, y) => x.month.localeCompare(y.month));
}

/**
 * Portfolio monthly return from the performance series — the Excel E-column:
 * (volume_t + withdrawals_t − deposits_t) / volume_{t−1} − 1.
 */
export function monthlyReturns(
  perf: { date: string; asset_volume: number | null; deposits: number | null; withdrawals: number | null }[],
): { date: string; ret: number | null }[] {
  const sorted = [...perf].sort((a, b) => a.date.localeCompare(b.date));
  return sorted.map((p, i) => {
    const prev = sorted[i - 1];
    if (!prev || !prev.asset_volume || p.asset_volume === null) return { date: p.date, ret: null };
    const ret = (p.asset_volume + (p.withdrawals ?? 0) - (p.deposits ?? 0)) / prev.asset_volume - 1;
    return { date: p.date, ret };
  });
}
